import { ToolKind } from "@frond/engine";
import { store } from "../state/store";

export interface ToolDef {
  kind: ToolKind;
  label: string;
  icon: string;
  key?: string;
  group: "draw" | "edit" | "insert" | "nav";
}

export const TOOLS: ToolDef[] = [
  { kind: "pen", label: "Pen", icon: "🖊️", key: "P", group: "draw" },
  { kind: "pencil", label: "Pencil", icon: "✏️", key: "G", group: "draw" },
  { kind: "marker", label: "Marker", icon: "🖍️", key: "M", group: "draw" },
  { kind: "highlighter", label: "Highlighter", icon: "🖌️", key: "H", group: "draw" },
  { kind: "eraser", label: "Eraser", icon: "🧽", key: "E", group: "draw" },
  { kind: "fill", label: "Fill", icon: "🪣", key: "F", group: "draw" },
  { kind: "select", label: "Select", icon: "▭", key: "V", group: "edit" },
  { kind: "lasso", label: "Lasso", icon: "〰️", key: "Q", group: "edit" },
  { kind: "line", label: "Line", icon: "╱", key: "L", group: "insert" },
  { kind: "rect", label: "Rectangle", icon: "▢", group: "insert" },
  { kind: "ellipse", label: "Ellipse", icon: "◯", key: "O", group: "insert" },
  { kind: "text", label: "Text", icon: "T", key: "T", group: "insert" },
  { kind: "image", label: "Image", icon: "🖼️", group: "insert" },
  { kind: "eyedropper", label: "Eyedropper", icon: "💧", key: "I", group: "edit" },
  { kind: "bookmark", label: "Bookmark", icon: "🔖", key: "B", group: "nav" },
];

export interface PaletteEntry {
  name: string;
  colors: string[];
}

export const DEFAULT_PALETTES: PaletteEntry[] = [
  {
    name: "Ink",
    colors: ["#1a1a1a", "#3a3a3a", "#6b6b6b", "#9c9c9c", "#ffffff"],
  },
  {
    name: "Vivid",
    colors: ["#e5484d", "#f76b15", "#ffd60a", "#30a46c", "#0091ff", "#8e4ec6", "#e93d82"],
  },
  {
    name: "Pastel",
    colors: ["#ffd6e0", "#ffe8c7", "#fff9c2", "#d3f3c0", "#c4e8ff", "#e3d1ff"],
  },
];

export function loadPalettes(): PaletteEntry[] {
  try {
    const raw = localStorage.getItem("frond.palettes");
    return raw ? (JSON.parse(raw) as PaletteEntry[]) : DEFAULT_PALETTES;
  } catch {
    return DEFAULT_PALETTES;
  }
}

export function savePalettes(palettes: PaletteEntry[]): void {
  localStorage.setItem("frond.palettes", JSON.stringify(palettes));
}

export function saveActivePalette(colors: string[]): void {
  localStorage.setItem("frond.palette", JSON.stringify(colors));
}

export function loadActivePalette(): string[] {
  try {
    const raw = localStorage.getItem("frond.palette");
    if (raw) {
      const parsed = JSON.parse(raw) as string[];
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch {
    /* fall through */
  }
  return DEFAULT_PALETTES[0]!.colors;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export async function pickImageFile(): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    };
    input.click();
  });
}

export { store };
